import { useMemo, useState } from 'react';

import { useAppSelector } from '@/store/hooks';
import { useGetDefaultTransmissionReportIdQuery } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice.ts';

import { ReportColor, SignatureStatus } from '@/types/transmissionReport';

/**
 * Custom hook to get the first default report IDs for navigation
 * @returns Object containing the next reportId and loading state
 */
export const useGetDefaultReportId = () => {
  const { signatureStatus } = useAppSelector((state) => state.auth);
  const { activeColorCategory } = useAppSelector((state) => state.transmissionReports);
  let isPendingReview = false;
  if (activeColorCategory === ReportColor.ReviewPending) isPendingReview = true;
  const { filters } = useAppSelector((state) => state.preferences);
  const LOCAL_STORAGE_KEY = `reportListSortOrder${activeColorCategory}`;
  const [sortOrder] = useState<null | 'ASC' | 'DESC'>(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored === 'ASC' || stored === 'DESC') return stored;
    return null;
  });
  const filterValues = useMemo(() => {
    return {
      clinicIds: filters?.clinics.map((clinic) => clinic.value) ?? [],
      practitionerIds: filters?.practitioners.map((practitioner) => practitioner.value) ?? [],
      signingPractitionerIds:
        filters?.signingPractitioners?.map((signingPractitioner) => signingPractitioner.value) ??
        [],
      deviceTypes: filters?.deviceTypes.map((deviceType) => deviceType.value) ?? [],
      vendors: filters?.vendors.map((vendor) => vendor.value) ?? [],
      sort: sortOrder ? { column: 'DATE_OF_SERVICE', order: sortOrder } : undefined
    };
  }, [filters, sortOrder]);

  // Fetch reports with a higher limit to ensure we have enough
  const queryParams = useMemo(
    () => ({
      signatureStatus: isPendingReview ? SignatureStatus.PendingReview : signatureStatus,
      color: isPendingReview ? ReportColor.Red : activeColorCategory,
      clinicIds: filterValues.clinicIds,
      practitionerIds: filterValues.practitionerIds,
      signingPractitionerIds: filterValues.signingPractitionerIds,
      deviceTypes: filterValues.deviceTypes,
      vendors: filterValues.vendors,
      sort: filterValues.sort
    }),
    [signatureStatus, activeColorCategory, filterValues, isPendingReview]
  );

  return useGetDefaultTransmissionReportIdQuery(queryParams);
};
